/**
 * Questionnaire service
 * Handles questionnaire CRUD operations and caching
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Questionnaire, Question } from '@/types';
import { cacheQuestionnaire, getCachedQuestionnaire } from '@/pwa/db';

/**
 * Create a new questionnaire
 */
export async function createQuestionnaire(
  orgId: string,
  title: string,
  questions: Question[]
): Promise<string> {
  const questionnaireRef = doc(collection(db, 'questionnaires'));
  const questionnaireId = questionnaireRef.id;

  const questionnaire: Questionnaire = {
    id: questionnaireId,
    orgId,
    title,
    isActive: false,
    questions,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await setDoc(questionnaireRef, questionnaire);

  return questionnaireId;
}

/**
 * Get questionnaire by ID
 * Falls back to cached version if offline
 */
export async function getQuestionnaire(
  questionnaireId: string
): Promise<Questionnaire | null> {
  try {
    const questionnaireRef = doc(db, 'questionnaires', questionnaireId);
    const snapshot = await getDoc(questionnaireRef);

    if (!snapshot.exists()) {
      return null;
    }

    const questionnaire = snapshot.data() as Questionnaire;

    // Cache for offline use
    await cacheQuestionnaire(questionnaireId, questionnaire);

    return questionnaire;
  } catch (error) {
    // If offline, try to get from cache
    if (!navigator.onLine) {
      const cached = await getCachedQuestionnaire(questionnaireId);
      return cached as Questionnaire | null;
    }
    throw error;
  }
}

/**
 * Get active questionnaire for organization
 */
export async function getActiveQuestionnaire(
  orgId: string
): Promise<Questionnaire | null> {
  try {
    const questionnairesRef = collection(db, 'questionnaires');
    const q = query(
      questionnairesRef,
      where('orgId', '==', orgId),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const questionnaire = snapshot.docs[0].data() as Questionnaire;

    // Cache for offline use
    await cacheQuestionnaire('active', questionnaire);

    return questionnaire;
  } catch (error) {
    // If offline, try to get from cache
    if (!navigator.onLine) {
      const cached = await getCachedQuestionnaire('active');
      return cached as Questionnaire | null;
    }
    throw error;
  }
}

/**
 * Get all questionnaires for organization
 */
export async function getQuestionnairesByOrg(
  orgId: string
): Promise<Questionnaire[]> {
  const questionnairesRef = collection(db, 'questionnaires');
  const q = query(questionnairesRef, where('orgId', '==', orgId));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data() as Questionnaire);
}

/**
 * Update questionnaire
 */
export async function updateQuestionnaire(
  questionnaireId: string,
  title: string,
  questions: Question[]
): Promise<void> {
  const questionnaireRef = doc(db, 'questionnaires', questionnaireId);
  await updateDoc(questionnaireRef, {
    title,
    questions,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Activate questionnaire
 * Deactivates all other questionnaires for the organization
 */
export async function activateQuestionnaire(
  orgId: string,
  questionnaireId: string
): Promise<void> {
  const batch = writeBatch(db);

  // Deactivate all questionnaires for this org
  const questionnairesRef = collection(db, 'questionnaires');
  const q = query(questionnairesRef, where('orgId', '==', orgId));
  const snapshot = await getDocs(q);

  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, { isActive: false });
  });

  // Activate the selected questionnaire
  const questionnaireRef = doc(db, 'questionnaires', questionnaireId);
  batch.update(questionnaireRef, {
    isActive: true,
    updatedAt: Timestamp.now(),
  });

  await batch.commit();

  // Update cache
  const questionnaire = await getQuestionnaire(questionnaireId);
  if (questionnaire) {
    await cacheQuestionnaire('active', questionnaire);
  }
}

/**
 * Delete questionnaire
 */
export async function deleteQuestionnaire(
  questionnaireId: string
): Promise<void> {
  const questionnaireRef = doc(db, 'questionnaires', questionnaireId);
  const snapshot = await getDoc(questionnaireRef);

  if (snapshot.exists()) {
    const data = snapshot.data() as Questionnaire;
    if (data.isActive) {
      throw new Error('Cannot delete active questionnaire');
    }
  }

  await deleteDoc(questionnaireRef);
}
